export interface FieldDef {
  key: string;
  label: string;
  description?: string;
  type?: string;
}

interface FieldSelectorProps {
  available: FieldDef[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

export function FieldSelector({ available, selected, onChange }: FieldSelectorProps) {
  const allSelected = available.length > 0 && selected.length === available.length;

  function toggleField(key: string) {
    if (selected.includes(key)) {
      onChange(selected.filter((k) => k !== key));
    } else {
      onChange([...selected, key]);
    }
  }

  function selectAll() {
    onChange(available.map((f) => f.key));
  }

  function clearAll() {
    onChange([]);
  }

  function moveUp(key: string) {
    const idx = selected.indexOf(key);
    if (idx <= 0) return;
    const next = [...selected];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  }

  function moveDown(key: string) {
    const idx = selected.indexOf(key);
    if (idx === -1 || idx >= selected.length - 1) return;
    const next = [...selected];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  }

  // Show selected fields first (in order), then unselected
  const orderedFields = [
    ...selected.map((key) => available.find((f) => f.key === key)).filter(Boolean) as FieldDef[],
    ...available.filter((f) => !selected.includes(f.key)),
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={allSelected ? clearAll : selectAll}
          className="text-xs font-medium text-brand-600 hover:text-brand-700"
        >
          {allSelected ? 'Clear all' : 'Select all'}
        </button>
        <span className="text-xs text-gray-400">
          {selected.length} of {available.length} selected
        </span>
      </div>

      <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-200">
        {orderedFields.map((field) => {
          const isSelected = selected.includes(field.key);
          const selectedIdx = selected.indexOf(field.key);
          return (
            <div
              key={field.key}
              className={`flex items-center gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0 ${
                isSelected ? 'bg-brand-50/50' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleField(field.key)}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{field.label}</p>
                {field.description && (
                  <p className="text-xs text-gray-500 truncate">{field.description}</p>
                )}
              </div>
              {field.type && (
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 flex-shrink-0">
                  {field.type}
                </span>
              )}
              {isSelected && (
                <div className="flex flex-shrink-0 gap-0.5">
                  <button
                    type="button"
                    onClick={() => moveUp(field.key)}
                    disabled={selectedIdx === 0}
                    className="rounded p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move up"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(field.key)}
                    disabled={selectedIdx === selected.length - 1}
                    className="rounded p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move down"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {available.length === 0 && (
          <p className="p-4 text-sm text-gray-500 text-center">No fields available. Select a data source first.</p>
        )}
      </div>
    </div>
  );
}
